// Client-side enhancements for clevcode.org. The site is fully functional
// without JavaScript; this layers on progressive enhancements:
//   - theme toggle (persisted, respects system preference)
//   - copy-to-clipboard buttons on code blocks
//   - threaded comment replies
//   - "soft" navigation: internal links swap <main> instead of reloading the
//     whole document (no asset re-parse, no flash; theme + dev socket persist)

type Theme = "light" | "dark";
const STORAGE_KEY = "theme";

// --- theme toggle (header is never swapped, so this binds once) ---

function currentTheme(): Theme {
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

function setupThemeToggle(): void {
  const button = document.querySelector<HTMLButtonElement>(".theme-toggle");
  if (!button) return;
  const sync = () => {
    const theme = currentTheme();
    button.setAttribute("aria-pressed", theme === "light" ? "true" : "false");
    button.dataset.theme = theme;
  };
  sync();
  button.addEventListener("click", () => {
    applyTheme(currentTheme() === "light" ? "dark" : "light");
    sync();
  });
}

// --- per-page enhancements (re-run after each soft navigation) ---

function setupCodeBlocks(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>("pre.chroma").forEach((pre) => {
    if (pre.parentElement?.classList.contains("code-wrap")) return;
    const wrap = document.createElement("div");
    wrap.className = "code-wrap";
    pre.parentNode?.insertBefore(wrap, pre);
    wrap.appendChild(pre);

    const button = document.createElement("button");
    button.type = "button";
    button.className = "copy-btn";
    button.textContent = "Copy";
    button.setAttribute("aria-label", "Copy code to clipboard");
    wrap.appendChild(button);

    button.addEventListener("click", async () => {
      try {
        // Copy the raw code, not pre.innerText: Chroma styles each line as
        // `.line { display: flex }`, so innerText adds a layout newline between
        // lines on top of the literal "\n" already inside each line — doubling
        // every newline. textContent ignores layout; strip the trailing newline
        // Chroma appends (EnsureNL) so paste has no dangling blank line.
        await navigator.clipboard.writeText((pre.textContent ?? "").replace(/\n$/, ""));
        button.textContent = "Copied";
        button.classList.add("copied");
        window.setTimeout(() => {
          button.textContent = "Copy";
          button.classList.remove("copied");
        }, 1600);
      } catch {
        button.textContent = "Error";
      }
    });

    addCodeScrollbar(wrap, pre);
  });
}

// addCodeScrollbar hides the (unstyleable, un-animatable) native horizontal
// scrollbar on a code block and replaces it with a thin custom pill: no arrows,
// and a smooth brighten-on-hover that native ::-webkit-scrollbar can't do.
function addCodeScrollbar(wrap: HTMLElement, pre: HTMLElement): void {
  const bar = document.createElement("div");
  bar.className = "cw-scrollbar";
  const thumb = document.createElement("div");
  thumb.className = "cw-thumb";
  bar.appendChild(thumb);
  wrap.appendChild(bar);

  const update = () => {
    const cw = pre.clientWidth;
    const sw = pre.scrollWidth;
    if (sw - cw <= 1) {
      wrap.classList.remove("has-scroll");
      return;
    }
    wrap.classList.add("has-scroll");
    const trackW = bar.clientWidth;
    const thumbW = Math.max(28, (cw / sw) * trackW);
    const pos = (pre.scrollLeft / (sw - cw)) * (trackW - thumbW);
    thumb.style.width = `${thumbW}px`;
    thumb.style.transform = `translateX(${pos}px)`;
  };

  pre.addEventListener("scroll", update, { passive: true });
  if (window.ResizeObserver) new ResizeObserver(update).observe(pre);
  window.addEventListener("resize", update);
  if (document.fonts?.ready) document.fonts.ready.then(update).catch(() => {});
  update();

  let dragging = false;
  let startX = 0;
  let startScroll = 0;
  thumb.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    dragging = true;
    startX = e.clientX;
    startScroll = pre.scrollLeft;
    thumb.setPointerCapture(e.pointerId);
  });
  thumb.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const maxThumb = bar.clientWidth - thumb.clientWidth;
    if (maxThumb <= 0) return;
    const range = pre.scrollWidth - pre.clientWidth;
    pre.scrollLeft = startScroll + ((e.clientX - startX) / maxThumb) * range;
  });
  const endDrag = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    try {
      thumb.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  thumb.addEventListener("pointerup", endDrag);
  thumb.addEventListener("pointercancel", endDrag);

  bar.addEventListener("pointerdown", (e) => {
    if (e.target === thumb) return;
    const frac = (e.clientX - bar.getBoundingClientRect().left) / bar.clientWidth;
    pre.scrollLeft = frac * (pre.scrollWidth - pre.clientWidth);
  });
}

function setupCommentReply(root: ParentNode): void {
  const form = root.querySelector<HTMLElement>("#respond");
  if (!form) return;
  const parentInput = form.querySelector<HTMLInputElement>(".cform-parent");
  const replyTo = form.querySelector<HTMLElement>(".cform-replyto");
  const replyName = form.querySelector<HTMLElement>(".cform-replyto-name");
  const cancel = form.querySelector<HTMLButtonElement>(".cform-cancel");
  const textarea = form.querySelector<HTMLTextAreaElement>("textarea");
  if (!parentInput || !replyTo || !replyName || !cancel) return;

  const anchor = document.createComment("respond-anchor");
  form.parentNode?.insertBefore(anchor, form);

  const reset = () => {
    parentInput.value = "0";
    replyTo.hidden = true;
    anchor.parentNode?.insertBefore(form, anchor.nextSibling);
  };

  root.querySelectorAll<HTMLButtonElement>(".comment-reply-link").forEach((btn) => {
    btn.addEventListener("click", () => {
      parentInput.value = btn.dataset.parent || "0";
      replyName.textContent = btn.dataset.author || "";
      replyTo.hidden = false;
      btn.closest(".comment")?.appendChild(form);
      textarea?.focus();
      form.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  });

  cancel.addEventListener("click", reset);
}

function enhance(root: ParentNode): void {
  setupCodeBlocks(root);
  setupCommentReply(root);
}

// --- soft navigation ---

const htmlCache = new Map<string, string>();
const prefetched = new Set<string>();
const MAX_CACHE = 40;

function reducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function cacheSet(key: string, html: string): void {
  if (htmlCache.size >= MAX_CACHE) {
    const oldest = htmlCache.keys().next().value;
    if (oldest !== undefined) htmlCache.delete(oldest);
  }
  htmlCache.set(key, html);
}

// eligible reports whether a link should be handled as an in-page navigation.
// Content pages all have paths ending in "/"; assets, downloads and feeds don't,
// so they fall through to normal browser handling.
function eligible(a: HTMLAnchorElement): boolean {
  if (a.target && a.target !== "_self") return false;
  if (a.hasAttribute("download")) return false;
  if ((a.getAttribute("rel") || "").includes("external")) return false;
  let url: URL;
  try {
    url = new URL(a.href, location.href);
  } catch {
    return false;
  }
  if (url.origin !== location.origin) return false;
  return url.pathname.endsWith("/");
}

let progressEl: HTMLElement | null = null;
function progress(state: "start" | "done"): void {
  if (!progressEl) {
    progressEl = document.createElement("div");
    progressEl.className = "nav-progress";
    document.body.appendChild(progressEl);
  }
  if (state === "start") {
    progressEl.classList.remove("done");
    // restart the animation
    void progressEl.offsetWidth;
    progressEl.classList.add("active");
  } else {
    progressEl.classList.remove("active");
    progressEl.classList.add("done");
  }
}

function prefetch(key: string, href: string): void {
  if (prefetched.has(key) || htmlCache.has(key)) return;
  prefetched.add(key);
  fetch(href, { credentials: "same-origin" })
    .then((r) => (r.ok && (r.headers.get("content-type") || "").includes("text/html") ? r.text() : null))
    .then((t) => {
      if (t) cacheSet(key, t);
    })
    .catch(() => {
      /* ignore */
    });
}

function syncHead(doc: Document): void {
  document.title = doc.title;
  const copy = (selector: string, attr: string) => {
    const from = doc.querySelector(selector);
    const to = document.querySelector(selector);
    if (from && to) to.setAttribute(attr, from.getAttribute(attr) || "");
  };
  copy('meta[name="description"]', "content");
  copy('link[rel="canonical"]', "href");
  copy('meta[property="og:title"]', "content");
  copy('meta[property="og:description"]', "content");
  copy('meta[property="og:url"]', "content");
}

// syncNav copies the server-computed active-nav state from the fetched page.
function syncNav(doc: Document): void {
  const active = new Set<string>();
  doc.querySelectorAll<HTMLAnchorElement>(".site-nav a[aria-current]").forEach((a) => {
    active.add(a.getAttribute("href") || "");
  });
  document.querySelectorAll<HTMLAnchorElement>(".site-nav a").forEach((a) => {
    if (active.has(a.getAttribute("href") || "")) a.setAttribute("aria-current", "page");
    else a.removeAttribute("aria-current");
  });
}

let navToken = 0;

async function softNavigate(href: string, opts: { push: boolean; scrollY?: number }): Promise<void> {
  const url = new URL(href, location.href);
  const key = url.pathname + url.search;
  const token = ++navToken;
  progress("start");
  try {
    let html = htmlCache.get(key);
    if (html === undefined) {
      const res = await fetch(url.href, { credentials: "same-origin" });
      if (!res.ok || !(res.headers.get("content-type") || "").includes("text/html")) {
        location.assign(url.href);
        return;
      }
      html = await res.text();
      cacheSet(key, html);
    }
    if (token !== navToken) return; // a newer navigation superseded this one

    const doc = new DOMParser().parseFromString(html, "text/html");
    const newMain = doc.querySelector("main");
    const curMain = document.querySelector("main");
    if (!newMain || !curMain) {
      location.assign(url.href);
      return;
    }

    if (opts.push) {
      // remember where we were, so Back can restore the scroll position
      history.replaceState({ scrollY: window.scrollY }, "");
    }

    const apply = () => {
      curMain.innerHTML = newMain.innerHTML;
      syncHead(doc);
      syncNav(doc);
      enhance(curMain);
      curMain.setAttribute("tabindex", "-1");
    };

    const vt = (document as unknown as { startViewTransition?: (cb: () => void) => void }).startViewTransition;
    if (vt && !reducedMotion()) vt.call(document, apply);
    else apply();

    if (opts.push) history.pushState({ scrollY: 0 }, "", url.href);

    if (url.hash) {
      const target = document.querySelector(url.hash);
      if (target) target.scrollIntoView();
      else window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, opts.scrollY ?? 0);
    }
    (curMain as HTMLElement).focus({ preventScroll: true });
  } catch {
    location.assign(url.href);
  } finally {
    if (token === navToken) progress("done");
  }
}

function setupSoftNav(): void {
  if (!window.history.pushState || !window.fetch || !window.DOMParser) return;
  history.scrollRestoration = "manual";
  history.replaceState({ scrollY: window.scrollY }, "");

  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element).closest?.("a");
    if (!a || !eligible(a as HTMLAnchorElement)) return;
    const url = new URL((a as HTMLAnchorElement).href, location.href);
    if (url.pathname === location.pathname && url.hash) return; // same-page anchor: native scroll
    e.preventDefault();
    if (url.href === location.href) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    void softNavigate(url.href, { push: true });
  });

  document.addEventListener(
    "mouseover",
    (e) => {
      const a = (e.target as Element)?.closest?.("a");
      if (a && eligible(a as HTMLAnchorElement)) {
        const url = new URL((a as HTMLAnchorElement).href, location.href);
        prefetch(url.pathname + url.search, url.href);
      }
    },
    { passive: true },
  );

  window.addEventListener("popstate", (e) => {
    const scrollY = (e.state && (e.state as { scrollY?: number }).scrollY) || 0;
    void softNavigate(location.href, { push: false, scrollY });
  });
}

function main(): void {
  setupThemeToggle();
  enhance(document);
  setupSoftNav();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", main);
} else {
  main();
}
